export type ReminderPreset = '1_day' | '2_days' | 'weekday_time';

export type WeekdayName =
  | 'sunday'
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday';

export interface ReminderTiming {
  preset: ReminderPreset;
  weekday?: WeekdayName;
  localTime?: string;
}

export const WEEKDAYS: { value: WeekdayName; label: string }[] = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' },
];

export const PAST_REMINDER_MESSAGE =
  'That reminder time already passed. Pick a time that’s still coming up.';

export const AFTER_START_MESSAGE =
  'The reminder has to go out before the event starts.';

const WEEKDAY_FROM_SHORT: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

const OFFSET_MINUTES: Record<'1_day' | '2_days', number> = {
  '1_day': 1440,
  '2_days': 2880,
};

function pad2(value: number | string): string {
  return String(value).padStart(2, '0');
}

export function localToUTC(localDateTimeStr: string, tz: string): Date {
  const [datePart, timePart] = localDateTimeStr.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = (timePart || '00:00:00').split(':').map((s) => parseInt(s, 10) || 0);

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));
  const parts = formatter.formatToParts(utcDate);
  const getPart = (type: string) => parts.find((p) => p.type === type)?.value;
  const tzYear = parseInt(getPart('year') || '0', 10);
  const tzMonth = parseInt(getPart('month') || '0', 10);
  const tzDay = parseInt(getPart('day') || '0', 10);
  let tzHour = parseInt(getPart('hour') || '0', 10);
  if (tzHour === 24) tzHour = 0;
  const tzMinute = parseInt(getPart('minute') || '0', 10);
  const utcMs = Date.UTC(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0);
  const offsetMs = utcDate.getTime() - utcMs;
  return new Date(Date.UTC(year, month - 1, day, hours, minutes, 0) + offsetMs);
}

function utcToLocalParts(utcDate: Date, tz: string) {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(utcDate);
  const getPart = (type: string) => parts.find((p) => p.type === type)?.value;
  let hour = parseInt(getPart('hour') || '0', 10);
  if (hour === 24) hour = 0;
  return {
    year: parseInt(getPart('year') || '0', 10),
    month: parseInt(getPart('month') || '0', 10),
    day: parseInt(getPart('day') || '0', 10),
    hour,
    minute: parseInt(getPart('minute') || '0', 10),
    weekday: WEEKDAY_FROM_SHORT[getPart('weekday') || 'Sun'] ?? 0,
  };
}

function addCalendarDays(year: number, month: number, day: number, delta: number) {
  const date = new Date(Date.UTC(year, month - 1, day + delta));
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
  };
}

function weekdayMinutes(localTime: string): number {
  const [hour, minute] = localTime.split(':').map(Number);
  return hour * 60 + minute;
}

/** True when that weekday+time in the event's week would be at or after start. */
export function weekdayFallsAfterEvent(
  startTime: Date | string,
  timezone: string,
  weekday: WeekdayName,
  localTime: string
): boolean {
  const start = startTime instanceof Date ? startTime : new Date(startTime);
  if (Number.isNaN(start.getTime())) return false;
  const targetDow = WEEKDAYS.findIndex((d) => d.value === weekday);
  if (targetDow < 0) return false;
  const eventLocal = utcToLocalParts(start, timezone);
  const daysBack = (eventLocal.weekday - targetDow + 7) % 7;
  if (daysBack > 0) return true;
  const eventMinutes = eventLocal.hour * 60 + eventLocal.minute;
  return weekdayMinutes(localTime) >= eventMinutes;
}

function computeWeekdayTimeSendAt(
  startTime: Date,
  timezone: string,
  weekday: WeekdayName,
  localTime: string
): Date {
  const targetDow = WEEKDAYS.findIndex((d) => d.value === weekday);
  const eventLocal = utcToLocalParts(startTime, timezone);
  let daysBack = (eventLocal.weekday - targetDow + 7) % 7;
  const eventMinutes = eventLocal.hour * 60 + eventLocal.minute;
  if (daysBack === 0 && weekdayMinutes(localTime) >= eventMinutes) {
    daysBack = 7;
  }
  const localDate = addCalendarDays(eventLocal.year, eventLocal.month, eventLocal.day, -daysBack);
  return localToUTC(
    `${localDate.year}-${pad2(localDate.month)}-${pad2(localDate.day)}T${localTime}:00`,
    timezone
  );
}

export function resolveReminderTiming(input: {
  preset: ReminderPreset;
  weekday?: WeekdayName;
  localTime?: string;
  startTime: Date | string;
  timezone: string;
  now?: Date;
  allowPast?: boolean;
}): { ok: true; sendAt: Date; alreadyPast?: boolean; afterEvent?: boolean } | { ok: false; error: string } {
  const startTime = input.startTime instanceof Date ? input.startTime : new Date(input.startTime);
  const now = input.now ?? new Date();
  if (Number.isNaN(startTime.getTime())) {
    return { ok: false, error: PAST_REMINDER_MESSAGE };
  }

  let sendAt: Date;
  let afterEvent = false;
  if (input.preset === '1_day' || input.preset === '2_days') {
    sendAt = new Date(startTime.getTime() - OFFSET_MINUTES[input.preset] * 60 * 1000);
  } else {
    if (!input.weekday || !input.localTime) {
      return { ok: false, error: PAST_REMINDER_MESSAGE };
    }
    afterEvent = weekdayFallsAfterEvent(
      startTime,
      input.timezone,
      input.weekday,
      input.localTime
    );
    sendAt = computeWeekdayTimeSendAt(startTime, input.timezone, input.weekday, input.localTime);
  }

  if (sendAt.getTime() >= startTime.getTime()) {
    return { ok: false, error: AFTER_START_MESSAGE };
  }
  if (sendAt.getTime() <= now.getTime()) {
    if (!input.allowPast) {
      return { ok: false, error: PAST_REMINDER_MESSAGE };
    }
    return { ok: true, sendAt, alreadyPast: true, afterEvent };
  }
  return { ok: true, sendAt, afterEvent };
}

export function isPresetAvailable(
  preset: ReminderPreset,
  startTime: Date | string,
  timezone: string,
  extras?: { weekday?: WeekdayName; localTime?: string; now?: Date }
): boolean {
  return resolveReminderTiming({
    preset,
    weekday: extras?.weekday ?? 'monday',
    localTime: extras?.localTime ?? '08:00',
    startTime,
    timezone,
    now: extras?.now,
    allowPast: true,
  }).ok;
}

export function reminderAlreadySent(event?: {
  reminder_sent_at?: string | null;
  reminder_schedule?: unknown;
} | null): boolean {
  if (!event) return false;
  if (event.reminder_sent_at) return true;
  const schedule = event.reminder_schedule;
  const item = Array.isArray(schedule)
    ? schedule[0]
    : schedule && typeof schedule === 'object'
      ? (schedule as { sent?: boolean })
      : null;
  return item?.sent === true;
}

export function formatSendPreview(sendAt: Date, timezone: string): string {
  return sendAt.toLocaleString('en-US', {
    timeZone: timezone,
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function reminderHelperCopy(
  resolved:
    | { ok: true; sendAt: Date; alreadyPast?: boolean; afterEvent?: boolean }
    | { ok: false; error: string },
  timezone: string,
  alreadySent = false
): { text: string; error: boolean } {
  if (alreadySent) {
    return { text: 'This reminder already went out', error: false };
  }
  if (!resolved.ok) {
    return { text: resolved.error, error: true };
  }
  if (resolved.afterEvent) {
    return { text: "That's after the event. We'll email them 24 hours prior.", error: false };
  }
  if (resolved.alreadyPast) {
    return { text: "We'll email them 24 hours prior.", error: false };
  }
  return {
    text: `We'll email them ${formatSendPreview(resolved.sendAt, timezone)}`,
    error: false,
  };
}

export function timingFromSchedule(schedule: unknown): ReminderTiming {
  const item = Array.isArray(schedule)
    ? schedule[0]
    : schedule && typeof schedule === 'object'
      ? (schedule as Record<string, unknown>)
      : null;
  if (!item) return { preset: '1_day', weekday: 'monday', localTime: '08:00' };
  const preset = item.preset as ReminderPreset | undefined;
  const mode = item.mode as string | undefined;
  if (preset === 'weekday_time' || mode === 'weekday_time') {
    return {
      preset: 'weekday_time',
      weekday: (item.weekday as WeekdayName) || 'monday',
      localTime: (item.local_time as string) || (item.localTime as string) || '08:00',
    };
  }
  if (preset === '2_days' || item.minutes_before === 2880) {
    return { preset: '2_days', weekday: 'monday', localTime: '08:00' };
  }
  return { preset: '1_day', weekday: 'monday', localTime: '08:00' };
}

export function timingFromEvent(event: {
  reminder_timing?: ReminderTiming | { preset?: string; weekday?: string; localTime?: string };
  reminder_schedule?: unknown;
  start_time?: string;
  timezone?: string;
}): ReminderTiming {
  const fromSchedule = timingFromSchedule(event.reminder_schedule);
  if (fromSchedule.preset === 'weekday_time' || fromSchedule.preset === '2_days') {
    return fromSchedule;
  }
  const saved = event.reminder_timing;
  if (saved?.preset === 'weekday_time' || saved?.preset === '2_days' || saved?.preset === '1_day') {
    return {
      preset: saved.preset,
      weekday: (saved.weekday as WeekdayName) || 'monday',
      localTime: saved.localTime || '08:00',
    };
  }
  if (event.start_time) {
    return defaultReminderTiming(event.start_time, event.timezone || 'America/Chicago');
  }
  return fromSchedule;
}

export function defaultReminderTiming(
  _startTime?: Date | string,
  _timezone?: string
): ReminderTiming {
  return { preset: '1_day', weekday: 'monday', localTime: '08:00' };
}

export function timingsEqual(a?: ReminderTiming | null, b?: ReminderTiming | null): boolean {
  const left = a || { preset: '1_day' as const };
  const right = b || { preset: '1_day' as const };
  if (left.preset !== right.preset) return false;
  if (left.preset !== 'weekday_time') return true;
  return (
    (left.weekday || undefined) === (right.weekday || undefined) &&
    (left.localTime || undefined) === (right.localTime || undefined)
  );
}

export function reminderTimingPayload(timing: ReminderTiming): ReminderTiming {
  if (timing.preset === 'weekday_time') {
    return {
      preset: 'weekday_time',
      weekday: timing.weekday ?? 'monday',
      localTime: timing.localTime ?? '08:00',
    };
  }
  return { preset: timing.preset };
}
